import type { ReactNode } from "react";
import { Children, isValidElement } from "react";
import { Link } from "@tanstack/react-router";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { CodeBlock } from "@/landing/docs/CodeBlock";
import { createHeadingIds } from "@/landing/docs/docs-nav";
import { EndpointHeader } from "@/landing/docs/EndpointHeader";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

type CodeProps = { className?: string; children?: ReactNode };

const ENDPOINT_RE = /^(GET|POST|PUT|PATCH|DELETE)\s+(\/\S+)$/;
// Generated pages open with an HTML comment for maintainers; it isn't content.
const HTML_COMMENT_RE = /<!--[\s\S]*?-->/g;

function extractLanguage(className: string | undefined): string | undefined {
  const match = className?.match(/language-(\S+)/);
  return match?.[1];
}

// Break opportunities inside inline code: after URL and identifier separators,
// so a URL or a dotted name can wrap there in a narrow table cell.
const CODE_BREAK_AFTER = /(?<=[/._\-?&=:,])/;

function withBreaks(text: string): ReactNode[] {
  return text
    .split(CODE_BREAK_AFTER)
    .flatMap((part, index) =>
      index === 0 ? [part] : [<wbr key={index} />, part],
    );
}

function textOf(node: ReactNode): string {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node))
    return (node as ReactNode[]).map((child) => textOf(child)).join("");
  if (isValidElement<{ children?: ReactNode }>(node))
    return textOf(node.props.children);
  return "";
}

type HastNode = {
  type: string;
  value?: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

function hastText(node: HastNode): string {
  if (node.type === "text") return node.value ?? "";
  return (node.children ?? []).map(hastText).join("");
}

/**
 * Gives h2 to h4 the ids extractHeadings computes, in document order. A rehype
 * step rather than component code so ids don't depend on how often React
 * renders a heading.
 */
function rehypeHeadingIds() {
  return (tree: HastNode) => {
    const nextId = createHeadingIds();
    const walk = (node: HastNode) => {
      for (const child of node.children ?? []) {
        if (child.type !== "element") continue;
        if (child.tagName && /^h[2-4]$/.test(child.tagName)) {
          child.properties = {
            ...child.properties,
            id: nextId(hastText(child)),
          };
        } else {
          walk(child);
        }
      }
    };
    walk(tree);
  };
}

/**
 * Copies each column's header text onto its body cells as data-label, which
 * .g-table-stack shows beside the value when a table stacks on phones.
 */
function rehypeTableLabels() {
  return (tree: HastNode) => {
    const walk = (node: HastNode) => {
      for (const child of node.children ?? []) {
        if (child.type !== "element") continue;
        if (child.tagName !== "table") {
          walk(child);
          continue;
        }
        const rows: HastNode[] = [];
        const collect = (parent: HastNode) => {
          for (const part of parent.children ?? []) {
            if (part.tagName === "tr") rows.push(part);
            else if (part.tagName === "thead" || part.tagName === "tbody")
              collect(part);
          }
        };
        collect(child);
        const cells = (row: HastNode) =>
          (row.children ?? []).filter(
            (cell) => cell.tagName === "th" || cell.tagName === "td",
          );
        const header = rows.find((row) =>
          cells(row).some((cell) => cell.tagName === "th"),
        );
        const labels = header ? cells(header).map(hastText) : [];
        for (const row of rows) {
          if (row === header) continue;
          cells(row).forEach((cell, index) => {
            const label = labels[index];
            if (label)
              cell.properties = { ...cell.properties, dataLabel: label };
          });
        }
      }
    };
    walk(tree);
  };
}

/**
 * Docs markdown. Renders plain elements for .g-prose (DocPage wraps it) and
 * swaps in the Grid parts: fenced code as CodeFrame, a lone `METHOD /path`
 * paragraph as an EndpointHeader, tables in a scroll wrapper. Headings get ids
 * that match extractHeadings (rehypeHeadingIds), for "On this page" links.
 */
export function MarkdownRenderer({ content }: { content: string }) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeHeadingIds, rehypeTableLabels]}
      components={{
        pre({ children }) {
          const items = Children.toArray(children);
          if (items.length === 1 && isValidElement<CodeProps>(items[0])) {
            const codeEl = items[0];
            const language = extractLanguage(codeEl.props.className);
            const code = textOf(codeEl.props.children).replace(/\n$/, "");
            return (
              <CodeBlock
                code={code}
                language={language}
                className="my-[1.6em]"
              />
            );
          }
          return <pre>{children}</pre>;
        },
        // Inline code only: fenced blocks are taken whole by `pre` above.
        code({ children, node: _node, ...rest }) {
          return (
            <code {...rest}>
              {typeof children === "string" ? withBreaks(children) : children}
            </code>
          );
        },
        p({ children, node: _node, ...rest }) {
          const items = Children.toArray(children);
          if (items.length === 1 && isValidElement<CodeProps>(items[0])) {
            const match = ENDPOINT_RE.exec(textOf(items[0].props.children));
            if (match) {
              return (
                <EndpointHeader
                  method={match[1] as HttpMethod}
                  path={match[2] ?? ""}
                />
              );
            }
          }
          return <p {...rest}>{children}</p>;
        },
        table({ node: _node, ...rest }) {
          return (
            <div className="g-table-scroll g-table-stack">
              <table {...rest} />
            </div>
          );
        },
        a({ href, children, node: _node, ...rest }) {
          if (!href) return <a {...rest}>{children}</a>;
          if (/^https?:\/\//.test(href)) {
            return (
              <a href={href} target="_blank" rel="noopener noreferrer">
                {children}
              </a>
            );
          }
          if (href.startsWith("#") || href.startsWith("mailto:")) {
            return <a href={href}>{children}</a>;
          }
          // Content links either as "/docs/<slug>" or as a bare "<slug>".
          return (
            <Link to={href.startsWith("/") ? href : `/docs/${href}`}>
              {children}
            </Link>
          );
        },
      }}
    >
      {content.replace(HTML_COMMENT_RE, "")}
    </Markdown>
  );
}
